// AIF Path B Wave 1, applied to GHL-Lite: every read/write to the
// console backend goes through a typed TanStack Query hook here.
//
// Rules:
//   - Components NEVER call api.x() directly.
//   - Stable query keys come from ./keys.ts.
//   - No `refetchInterval` here. The global staleTime + window-focus
//     defaults in main.tsx drive freshness; mutations invalidate.
//
// See ./README.md.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { ApiClient } from "../api";
import type { AddCustomerRequest } from "../types";
import { consoleKeys } from "./keys";

export function useAuthConfigQuery(api: ApiClient) {
  return useQuery({
    queryKey: consoleKeys.authConfig(),
    queryFn: () => api.getAuthConfig(),
    staleTime: Infinity,
    retry: false,
  });
}

export function useStatusQuery(api: ApiClient) {
  return useQuery({
    queryKey: consoleKeys.status(),
    queryFn: () => api.getStatus(),
  });
}

export function useGhlStatusQuery(api: ApiClient) {
  return useQuery({
    queryKey: consoleKeys.ghlStatus(),
    queryFn: () => api.getGhlStatus(),
  });
}

export function useSitesQuery(api: ApiClient) {
  return useQuery({
    queryKey: consoleKeys.sites(),
    queryFn: () => api.getSites(),
  });
}

export function useIngestStatusQuery(api: ApiClient) {
  return useQuery({
    queryKey: consoleKeys.ingestStatus(),
    queryFn: () => api.getIngestStatus(),
  });
}

export function useCheckConnectionMutation(api: ApiClient) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.runCheckConnection(),
    // Invalidating the upstream reads after a successful check refreshes
    // the panels with whatever state changed inside the audit window.
    // No timer-based polling needed; mutations are the cache trigger.
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: consoleKeys.ghlStatus() });
      queryClient.invalidateQueries({ queryKey: consoleKeys.sites() });
      queryClient.invalidateQueries({ queryKey: consoleKeys.status() });
    },
  });
}

export function useAddCustomerMutation(api: ApiClient) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: AddCustomerRequest) => api.addCustomer(input),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: consoleKeys.sites() });
      queryClient.invalidateQueries({ queryKey: consoleKeys.ingestStatus() });
    },
  });
}
